import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ReactLenis, useLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Partner from "@/pages/Partner";
import News from "@/pages/News";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import { AuthProvider, ProtectedRoute } from "@/app/AuthContext";
import { AppShell } from "@/app/AppShell";
import Login from "@/app/pages/Login";
import Register from "@/app/pages/Register";
import { LanguageProvider } from "@/app/LanguageContext";
import Dashboard from "@/app/pages/Dashboard";
import Discover from "@/app/pages/Discover";
import Applications from "@/app/pages/Applications";
import ApplicationDetail from "@/app/pages/ApplicationDetail";
import Funding from "@/app/pages/Funding";
import Documents from "@/app/pages/Documents";
import Profile from "@/app/pages/Profile";
import SavedOpportunities from "@/app/pages/SavedOpportunities";
import CompareOpportunities from "@/app/pages/CompareOpportunities";
import EligibilityAssessments from "@/app/pages/EligibilityAssessments";
import Partners from "@/app/pages/admin/Partners";
import PartnerConfig from "@/app/pages/admin/PartnerConfig";
import CallRequests from "@/app/pages/admin/CallRequests";
import PartnerApplications from "@/app/pages/partner/PartnerApplications";
import PartnerApplicationDetail from "@/app/pages/partner/PartnerApplicationDetail";
import CommissionList from "@/app/pages/commissions/CommissionList";
import CommissionDetail from "@/app/pages/commissions/CommissionDetail";
import OpportunityDetails from "@/pages/OpportunityDetails";
import Journey from "@/pages/Journey";
import EligibilityCheck from "@/pages/EligibilityCheck";
import AffiliateDashboard from "@/app/pages/affiliate/AffiliateDashboard";
import Affiliates from "@/app/pages/admin/Affiliates";
import AffiliateDetail from "@/app/pages/admin/AffiliateDetail";
import AffiliateProgramme from "@/app/pages/admin/AffiliateProgramme";
import Entitlements from "@/app/pages/admin/Entitlements";
import EntitlementDetail from "@/app/pages/admin/EntitlementDetail";
import BenefitSettings from "@/app/pages/admin/BenefitSettings";
import Reporting from "@/app/pages/admin/Reporting";
import { captureReferralFromUrl } from "@/lib/referral";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function LenisBridge() {
  const lenis = useLenis();
  useEffect(() => { window.__lenis = lenis; }, [lenis]);
  return null;
}

function ReferralCapture() {
  const { search } = useLocation();
  useEffect(() => { captureReferralFromUrl(); }, [search]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <BrowserRouter>
        <ScrollToTop />
        <LenisBridge />
        <ReferralCapture />
        <LanguageProvider>
        <AuthProvider>
          <Routes>
            {/* Public marketing */}
            <Route path="/" element={<Home />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/news" element={<News />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/opportunities/:slug" element={<OpportunityDetails />} />
            <Route path="/journey" element={<Journey />} />
            <Route path="/eligibility/:slug" element={<EligibilityCheck />} />

            {/* Platform */}
            <Route path="/app/login" element={<Login />} />
            <Route path="/app/register" element={<Register />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/app/discover" element={<Discover />} />
                <Route path="/app/applications" element={<Applications />} />
                <Route path="/app/applications/:applicationId" element={<ApplicationDetail />} />
                <Route path="/app/funding" element={<Funding />} />
                <Route path="/app/documents" element={<Documents />} />
                <Route path="/app/profile" element={<Profile />} />
                <Route path="/app/saved" element={<SavedOpportunities />} />
                <Route path="/app/compare" element={<CompareOpportunities />} />
                <Route path="/app/assessments" element={<EligibilityAssessments />} />
                <Route path="/app/affiliate" element={<AffiliateDashboard />} />
                <Route element={<ProtectedRoute roles={["admin"]} />}>
                <Route path="/app/admin/partners" element={<Partners />} />
                <Route path="/app/admin/partners/new" element={<PartnerConfig />} />
                <Route path="/app/admin/partners/:partnerId" element={<PartnerConfig />} />
                <Route path="/app/admin/call-requests" element={<CallRequests />} />
                <Route path="/app/admin/commissions" element={<CommissionList />} />
                <Route path="/app/admin/commissions/:commissionId" element={<CommissionDetail />} />
                <Route path="/app/admin/affiliates" element={<Affiliates />} />
                <Route path="/app/admin/affiliates/programme" element={<AffiliateProgramme />} />
                <Route path="/app/admin/affiliates/:affiliateId" element={<AffiliateDetail />} />
                <Route path="/app/admin/entitlements" element={<Entitlements />} />
                <Route path="/app/admin/entitlements/settings" element={<BenefitSettings />} />
                <Route path="/app/admin/entitlements/:entitlementId" element={<EntitlementDetail />} />
                <Route path="/app/admin/reporting" element={<Reporting />} />
                </Route>
                <Route element={<ProtectedRoute roles={["partnership", "admin"]} />}>
                <Route path="/app/partner/applications" element={<PartnerApplications />} />
                <Route path="/app/partner/applications/:applicationId" element={<PartnerApplicationDetail />} />
                <Route path="/app/partner/commissions" element={<CommissionList />} />
                <Route path="/app/partner/commissions/:commissionId" element={<CommissionDetail />} />
                </Route>
              </Route>
            </Route>
          </Routes>
          <Toaster position="bottom-right" />
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ReactLenis>
    </HelmetProvider>
  );
}

export default App;
